import {
  Component,
  ElementRef,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  EventEmitter,
  Output,
} from '@angular/core';

@Component({
  selector: 'app-video-custom',
  templateUrl: './video-custom.component.html',
  styleUrls: ['./video-custom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoCustomComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() url: string = '';
  @Input() showControls: boolean = false;
  @Output() onLoadVideo: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('progressBar') progressBar!: ElementRef<HTMLDivElement>;

  public togglePlay: boolean = false;
  public toggleMute: boolean = true;
  public toggleFullscreen: boolean = false;

  public sizeIcon: number = 20;
  public durationVideo: number = 0;
  public currentTime: number = 0;
  public progress: number = 0;

  public isPlaying: boolean = false;
  public isDragging: boolean = false;

  public effectPlay: boolean = false;
  public effectPause: boolean = false;

  // Guardamos las referencias a las funciones bound
  private boundLoadVideo: () => void;
  private boundTimeUpdate: () => void;
  private boundVolumeChange: () => void;
  private boundPlayEvent: () => void;
  private boundPauseEvent: () => void;

  private fullscreenChangeHandler: () => void;

  private readonly DEFAULT_VOLUME = 0.25; // Constante para el volumen por defecto

  constructor(private cdr: ChangeDetectorRef) {
    // Creamos las funciones bound una sola vez
    this.boundLoadVideo = this.loadVideo.bind(this);
    this.boundTimeUpdate = this.timeUpdate.bind(this);
    this.fullscreenChangeHandler = this.onFullscreenChange.bind(this);
    this.boundVolumeChange = this.onVolumeChange.bind(this);
    this.boundPlayEvent = this.onPlayEvent.bind(this);
    this.boundPauseEvent = this.onPauseEvent.bind(this);
  }

  ngOnInit(): void {
    this.initializeVolume();
    this.muteVideo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showControls']) {
      this.cdr.detectChanges();
    }
    if (changes['url']) {
      this.cdr.detectChanges();
    }
  }

  private debugAudioState(): void {
    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      console.log(`Video:`, {
        muted: video.muted,
        volume: video.volume,
        paused: video.paused,
        currentTime: video.currentTime,
        src: video.src,
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.videoPlayer) {
      this.initializeVolume();
      this.startEvents();
      this.setupFullscreenEvents();
    }
  }

  private loadVideo() {
    this.durationVideo = this.videoPlayer.nativeElement.duration;
    this.onLoadVideo.emit();
    this.cdr.detectChanges();
  }

  private timeUpdate() {
    this.currentTime = this.videoPlayer.nativeElement.currentTime;
    this.progress = (this.currentTime / this.durationVideo) * 100;
    this.cdr.markForCheck();
    if (this.currentTime === this.durationVideo && this.isDragging === false) {
      this.togglePlay = false;
      this.isPlaying = false;
      this.cdr.detectChanges();
    }
  }

  startEvents() {
    if (!this.videoPlayer?.nativeElement) return;

    this.clearEvents();

    const video = this.videoPlayer.nativeElement;
    video.addEventListener('loadedmetadata', this.boundLoadVideo);
    video.addEventListener('timeupdate', this.boundTimeUpdate);
    video.addEventListener('volumechange', this.boundVolumeChange);
    video.addEventListener('play', this.boundPlayEvent);
    video.addEventListener('pause', this.boundPauseEvent);

    // Debug para verificar eventos
    // this.debugEvents();
  }

  clearEvents() {
    if (!this.videoPlayer?.nativeElement) return;

    const video = this.videoPlayer.nativeElement;
    video.removeEventListener('loadedmetadata', this.boundLoadVideo);
    video.removeEventListener('timeupdate', this.boundTimeUpdate);
    video.removeEventListener('volumechange', this.boundVolumeChange);
    video.removeEventListener('play', this.boundPlayEvent);
    video.removeEventListener('pause', this.boundPauseEvent);

    // Debug para verificar eventos
    // this.debugEvents();
  }

  private debugEvents() {
    if (!this.videoPlayer?.nativeElement) return;

    const video = this.videoPlayer.nativeElement;

    // En Chrome/Firefox podemos usar getEventListeners (solo en consola de desarrollo)
    console.log('Current video element:', video);
    console.log('Video src:', video.src);

    // Podemos crear un flag para rastrear si los eventos están adjuntos
    console.log('Event status:', {
      hasLoadedMetadata: this.hasEventListener(video, 'loadedmetadata'),
      hasTimeUpdate: this.hasEventListener(video, 'timeupdate'),
    });
  }

  private hasEventListener(
    element: HTMLVideoElement,
    eventName: string
  ): boolean {
    // Creamos una función temporal para probar
    const testHandler = () => {};
    try {
      element.addEventListener(eventName, testHandler);
      element.removeEventListener(eventName, testHandler);
      return true;
    } catch (e) {
      // Si hay un error al agregar el evento, probablemente ya existe
      return false;
    }
  }

  private auxTogglePlay: boolean = false;

  startSeeking(event: MouseEvent) {
    event.preventDefault();
    this.isDragging = true;
    // Pausamos el video mientras se arrastra
    this.auxTogglePlay = this.togglePlay;
    if (this.togglePlay) {
      this.videoPlayer.nativeElement.pause();
    }

    // Actualizamos la posición inmediatamente al empezar
    this.updateVideoPosition(event);
  }

  // Movemos mousemove al window
  @HostListener('window:mousemove', ['$event'])
  seeking(event: MouseEvent) {
    if (this.isDragging) {
      this.updateVideoPosition(event);
    }
  }

  // Movemos mouseup al window
  @HostListener('window:mouseup')
  endSeeking() {
    if (this.isDragging) {
      this.isDragging = false;
      if (this.auxTogglePlay) {
        this.videoPlayer.nativeElement.play();
        this.togglePlay = true;
      }
    }
  }

  private updateVideoPosition(event: MouseEvent) {
    if (this.videoPlayer && this.progressBar) {
      const progressRect =
        this.progressBar.nativeElement.getBoundingClientRect();
      let clickPosition =
        (event.clientX - progressRect.left) / progressRect.width;

      // Limitamos el valor entre 0 y 1
      clickPosition = Math.max(0, Math.min(1, clickPosition));

      const newTime = this.durationVideo * clickPosition;
      this.videoPlayer.nativeElement.currentTime = newTime;
      this.progress = (newTime / this.durationVideo) * 100;
      this.currentTime = newTime;
      this.cdr.detectChanges();
    }
  }

  // Manejador de eventos de teclado a nivel de window
  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Solo procesar eventos si el componente está activo y el video es visible
    if (!this.isVideoVisible()) return;

    // Evitamos que los atajos afecten a otros elementos
    // faltaria testear si esto obstruye otro evento
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement
    )
      return;

    // Verificar si el video actual está en el viewport
    if (!this.isVideoInViewport()) return;

    switch (event.key.toLowerCase()) {
      case ' ': // Espacio
      case 'k':
        event.preventDefault(); // Prevenir scroll con espacio
        this.onTogglePlay();
        break;

      case 'j':
      case 'arrowleft':
        event.preventDefault();
        this.backwardVideo();
        break;

      case 'l':
      case 'arrowright':
        event.preventDefault();
        this.forwardVideo();
        break;

      case 'm':
        event.preventDefault();
        this.onToggleMute();
        break;

      // tiene bug todavia por angular supuestamente
      case 'f':
        event.preventDefault();
        this.onToggleFullscreen();
        break;
    }
  }

  normalizeTime(time: number) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  onTogglePlay() {
    this.togglePlay = !this.togglePlay;
    if (this.togglePlay) {
      this.playVideo();
    } else {
      this.pauseVideo();
    }
  }

  onToggleMute() {
    this.toggleMute = !this.toggleMute;
    if (this.toggleMute) {
      this.muteVideo();
    } else {
      this.unmuteVideo();
    }
    // this.debugAudioState();
  }

  onToggleFullscreen() {
    this.toggleFullscreen = !this.toggleFullscreen;
    if (this.toggleFullscreen) {
      this.fullscreenVideo();
    } else {
      this.exitFullscreenVideo();
    }
  }

  playVideo() {
    if (this.videoPlayer) {
      const video = this.videoPlayer.nativeElement;
      video
        .play()
        .then(() => {
          // El evento 'play' manejará la actualización del estado
        })
        .catch((error) => {
          console.error('Error al reproducir el video:', error);
          // Revertir el estado en caso de error
          this.togglePlay = false;
          this.isPlaying = false;
          this.cdr.detectChanges();
        });
    }
  }

  pauseVideo() {
    if (this.videoPlayer) {
      const video = this.videoPlayer.nativeElement;
      video.pause();
      // El evento 'pause' manejará la actualización del estado
    }
  }

  forwardVideo() {
    if (this.videoPlayer) {
      this.isPlaying = false;
      this.videoPlayer.nativeElement.currentTime += 5;

      if (this.togglePlay) {
        setTimeout(() => {
          this.isPlaying = true;
        }, 300);
      }
    }
  }

  backwardVideo() {
    if (this.videoPlayer) {
      this.isPlaying = false;
      this.videoPlayer.nativeElement.currentTime -= 5;

      if (this.togglePlay) {
        setTimeout(() => {
          this.isPlaying = true;
        }, 300);
      }
    }
  }

  muteVideo() {
    if (this.videoPlayer) {
      const video = this.videoPlayer.nativeElement;
      video.muted = true;
      this.toggleMute = true;
      // Guardar el volumen anterior antes de mutear
      if (!video.dataset.previousVolume) {
        video.dataset.previousVolume = video.volume.toString();
      }
      video.volume = 0;
      this.cdr.detectChanges();
    }
  }

  unmuteVideo() {
    if (this.videoPlayer) {
      const video = this.videoPlayer.nativeElement;
      video.muted = false;
      this.toggleMute = false;
      // Restaurar el volumen anterior o usar 1 como valor predeterminado
      const previousVolume = video.dataset.previousVolume
        ? parseFloat(video.dataset.previousVolume)
        : 1;
      video.volume = previousVolume;
      delete video.dataset.previousVolume;
      this.cdr.detectChanges();
    }
  }

  fullscreenVideo() {
    if (!this.videoPlayer?.nativeElement) return;

    const video = this.videoPlayer.nativeElement;

    try {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if ((video as any).webkitRequestFullscreen) {
        (video as any).webkitRequestFullscreen();
      } else if ((video as any).mozRequestFullScreen) {
        (video as any).mozRequestFullScreen();
      } else if ((video as any).msRequestFullscreen) {
        (video as any).msRequestFullscreen();
      }
    } catch (error) {
      console.error('Error al entrar en modo pantalla completa:', error);
    }
  }

  exitFullscreenVideo() {
    try {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    } catch (error) {
      console.error('Error al salir del modo pantalla completa:', error);
    }
  }

  removeEvent() {
    if (this.videoPlayer) {
      this.videoPlayer.nativeElement.removeEventListener(
        'loadedmetadata',
        () => {}
      );
      this.videoPlayer.nativeElement.removeEventListener(
        'timeupdate',
        () => {}
      );
    }
  }

  ngOnDestroy(): void {
    this.clearEvents();

    // Limpiar el video
    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      video.pause();
      video.src = '';
      video.load();

      // Limpiar el volumen guardado
      delete video.dataset.previousVolume;

      // Remover los event listeners de fullscreen
      video.removeEventListener(
        'fullscreenchange',
        this.fullscreenChangeHandler
      );
      video.removeEventListener(
        'webkitfullscreenchange',
        this.fullscreenChangeHandler
      );
      video.removeEventListener(
        'mozfullscreenchange',
        this.fullscreenChangeHandler
      );
      video.removeEventListener(
        'MSFullscreenChange',
        this.fullscreenChangeHandler
      );
    }
  }

  // DIOS GPT LO HIZO !!!

  // Método para verificar si el video está visible
  private isVideoVisible(): boolean {
    if (!this.videoPlayer?.nativeElement) return false;

    const rect = this.videoPlayer.nativeElement.getBoundingClientRect();
    const isVisible =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);

    return isVisible;
  }

  // Método para verificar si el video está en el viewport
  private isVideoInViewport(): boolean {
    if (!this.videoPlayer?.nativeElement) return false;

    const rect = this.videoPlayer.nativeElement.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth;

    // Consideramos que el video está "activo" si al menos el 50% está visible
    const visibleHeight =
      Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    const visibleWidth =
      Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);
    const visibleArea = visibleHeight * visibleWidth;
    const totalArea = rect.height * rect.width;

    return visibleArea / totalArea > 0.5; // 50% visible
  }

  private setupFullscreenEvents(): void {
    if (!this.videoPlayer?.nativeElement) return;

    const video = this.videoPlayer.nativeElement;

    // Agregar listeners para todos los prefijos de navegadores
    video.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    video.addEventListener(
      'webkitfullscreenchange',
      this.fullscreenChangeHandler
    );
    video.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler);
    video.addEventListener('MSFullscreenChange', this.fullscreenChangeHandler);
  }

  private onFullscreenChange(): void {
    // Verificar si el video está en pantalla completa
    const isFullscreen = this.isVideoFullscreen();

    if (this.toggleFullscreen !== isFullscreen) {
      this.toggleFullscreen = isFullscreen;
      this.cdr.detectChanges();
    }

    /* console.log('Fullscreen state changed:', {
      isFullscreen,
      element: document.fullscreenElement,
      video: this.videoPlayer?.nativeElement,
    }); */
  }

  private isVideoFullscreen(): boolean {
    if (!this.videoPlayer?.nativeElement) return false;

    const video = this.videoPlayer.nativeElement;

    // Verificar todos los posibles estados de pantalla completa
    return !!(
      document.fullscreenElement === video ||
      (document as any).webkitFullscreenElement === video ||
      (document as any).mozFullScreenElement === video ||
      (document as any).msFullscreenElement === video
    );
  }

  private onVolumeChange(): void {
    if (!this.videoPlayer?.nativeElement) return;

    const video = this.videoPlayer.nativeElement;
    // Actualizar el estado de mute basado en el estado real del video
    if (this.toggleMute !== video.muted) {
      this.toggleMute = video.muted;
      this.cdr.detectChanges();
    }
  }

  private onPlayEvent(): void {
    if (!this.videoPlayer?.nativeElement) return;

    this.togglePlay = true;
    this.isPlaying = true;
    this.effectPlay = true;
    this.effectPause = false;
    this.cdr.detectChanges();
  }

  private onPauseEvent(): void {
    if (!this.videoPlayer?.nativeElement) return;

    this.togglePlay = false;
    this.isPlaying = false;
    this.effectPause = true;
    this.effectPlay = false;
    this.cdr.detectChanges();
  }

  private initializeVolume(): void {
    if (this.videoPlayer?.nativeElement) {
      this.videoPlayer.nativeElement.volume = this.DEFAULT_VOLUME;
    }
  }
}
